/**
 * Web Audio 分析图：HTMLAudioElement → AnalyserNode → 扬声器。
 * 每个 audio 元素只能 createMediaElementSource 一次。
 */

export type AudioGraph = {
  context: AudioContext;
  analyser: AnalyserNode;
  freq: Uint8Array;
  time: Uint8Array;
  /** 用户手势后调用，解锁自动播放策略 */
  resume: () => Promise<void>;
  sample: () => void;
  dispose: () => void;
};

const graphs = new WeakMap<HTMLAudioElement, AudioGraph>();

export function getOrCreateAudioGraph(audio: HTMLAudioElement): AudioGraph {
  const existing = graphs.get(audio);
  if (existing) return existing;

  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const context = new AudioCtx();

  // 同源或已配 CORS 时可分析；跨域无 CORS 时频谱会是静音
  try {
    audio.crossOrigin = "anonymous";
  } catch {
    /* ignore */
  }

  const source = context.createMediaElementSource(audio);
  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.82;
  analyser.minDecibels = -90;
  analyser.maxDecibels = -10;

  source.connect(analyser);
  analyser.connect(context.destination);

  const freq = new Uint8Array(analyser.frequencyBinCount);
  const time = new Uint8Array(analyser.fftSize);

  const graph: AudioGraph = {
    context,
    analyser,
    freq,
    time,
    resume: async () => {
      if (context.state === "suspended") await context.resume();
    },
    sample: () => {
      analyser.getByteFrequencyData(freq);
      analyser.getByteTimeDomainData(time);
    },
    dispose: () => {
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        /* already disconnected */
      }
      void context.close();
      graphs.delete(audio);
    },
  };

  graphs.set(audio, graph);
  return graph;
}

/** 从频谱粗分低/中/高能量（0–1） */
export function bandEnergy(freq: Uint8Array): { bass: number; mid: number; treble: number; overall: number } {
  const n = freq.length;
  if (!n) return { bass: 0, mid: 0, treble: 0, overall: 0 };

  const bassEnd = Math.max(1, Math.floor(n * 0.08));
  const midEnd = Math.max(bassEnd + 1, Math.floor(n * 0.4));

  let bass = 0;
  let mid = 0;
  let treble = 0;
  let all = 0;
  for (let i = 0; i < n; i++) {
    const v = freq[i]! / 255;
    all += v;
    if (i < bassEnd) bass += v;
    else if (i < midEnd) mid += v;
    else treble += v;
  }
  return {
    bass: bass / bassEnd,
    mid: mid / (midEnd - bassEnd),
    treble: treble / (n - midEnd),
    overall: all / n,
  };
}
